import Image from '@tiptap/extension-image';
import { mergeAttributes } from '@tiptap/core';

export interface AttachmentImageInsertPayload {
  attachmentId: string;
  src?: string;
  url?: string;
}

export interface LinkedImageInsertPayload {
  url: string;
  /** Measured natural width of the remote image (px). */
  naturalWidth?: number | null;
  /** Measured natural height of the remote image (px). */
  naturalHeight?: number | null;
  /** Initial display width to apply (px, capped to 800). */
  width?: number | null;
  /** Initial display height to apply (px, aspect-ratio preserved). */
  height?: number | null;
}

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
  }
  return fallback;
};

const normalizeCssDimension = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return `${value}px`;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return `${trimmed}px`;
  }

  if (/^\d+(?:\.\d+)?(px|%|em|rem|vw|vh)$/.test(trimmed)) {
    return trimmed;
  }

  return null;
};

const extractStyleDimension = (element: HTMLElement, property: 'width' | 'height'): string | null => {
  const inlineValue = element.style.getPropertyValue(property)?.trim();
  if (inlineValue) {
    return inlineValue;
  }

  const styleAttr = element.getAttribute('style') || '';
  const match = styleAttr.match(new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i'));
  return match?.[1]?.trim() || null;
};

const parseDimensionToPx = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const plain = Number(trimmed);
  if (Number.isFinite(plain) && plain > 0) {
    return plain;
  }

  const pxMatch = trimmed.match(/^(\d+(?:\.\d+)?)px$/i);
  if (pxMatch) {
    const parsed = Number(pxMatch[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
};

type ImageHandleDirection = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    phase1Image: {
      insertLinkedImage: (payload: LinkedImageInsertPayload) => ReturnType;
      insertAttachmentImage: (attachmentResponse: AttachmentImageInsertPayload) => ReturnType;
    };
  }
}

export const Phase1Image = Image.extend({
  name: 'image',

  addNodeView() {
    return ({ node, getPos, editor }) => {
      let currentNode = node;

      const MIN_WIDTH = 50;
      const MIN_HEIGHT = 50;

      const wrapper = document.createElement('div');
      wrapper.className = 'tiptap-image-node';
      wrapper.contentEditable = 'false';

      const img = document.createElement('img');
      img.className = 'tiptap-image-node__img';
      img.draggable = false;
      img.setAttribute('draggable', 'false');
      wrapper.appendChild(img);

      const handleElements: HTMLSpanElement[] = [];
      const handleDirections: ImageHandleDirection[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
      const handleListeners: Array<{ el: HTMLElement; type: string; fn: EventListener }> = [];
      const addHandleListener = (el: HTMLElement, type: string, fn: EventListener): void => {
        el.addEventListener(type, fn);
        handleListeners.push({ el, type, fn });
      };

      for (const direction of handleDirections) {
        const handle = document.createElement('span');
        handle.className = `tiptap-image-node__handle tiptap-image-node__handle--${direction}`;
        handle.setAttribute('data-handle', direction);
        handle.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(handle);
        handleElements.push(handle);
      }

      const applyImageDomFromNode = (targetNode: typeof currentNode): void => {
        const syncAttr = (name: string, value: unknown): void => {
          const normalized = String(value ?? '').trim();
          if (normalized) {
            img.setAttribute(name, normalized);
          } else {
            img.removeAttribute(name);
          }
        };

        const src = String(targetNode.attrs['src'] ?? '').trim();
        if (img.getAttribute('src') !== src) {
          img.setAttribute('src', src);
        }

        // Preserve custom image metadata on the DOM node so existing editor-level
        // attachment hydration logic can resolve authenticated blobs.
        syncAttr('data-image-source-type', targetNode.attrs['imageSourceType']);
        syncAttr('data-attachment-id', targetNode.attrs['attachmentId']);
        syncAttr('data-original-url', targetNode.attrs['originalUrl']);
        syncAttr('data-natural-width', targetNode.attrs['naturalWidth']);
        syncAttr('data-natural-height', targetNode.attrs['naturalHeight']);
        img.setAttribute('data-keep-aspect-ratio', toBoolean(targetNode.attrs['keepAspectRatio'], true) ? 'true' : 'false');

        const alt = targetNode.attrs['alt'];
        if (typeof alt === 'string') {
          img.setAttribute('alt', alt);
        } else {
          img.removeAttribute('alt');
        }

        const title = targetNode.attrs['title'];
        if (typeof title === 'string') {
          img.setAttribute('title', title);
        } else {
          img.removeAttribute('title');
        }

        const widthPx = parseDimensionToPx(targetNode.attrs['width']);
        const heightPx = parseDimensionToPx(targetNode.attrs['height']);
        const keepAspectRatio = toBoolean(targetNode.attrs['keepAspectRatio'], true);
        img.style.maxWidth = '100%';
        img.style.width = widthPx ? `${Math.round(widthPx)}px` : '';
        img.style.height = keepAspectRatio ? 'auto' : (heightPx ? `${Math.round(heightPx)}px` : '');
      };

      const preventDrag = (event: Event): void => {
        event.preventDefault();
      };
      img.addEventListener('dragstart', preventDrag);

      const getMaxWidthPx = (): number => {
        const parent = wrapper.parentElement as HTMLElement | null;
        if (!parent) {
          return Number.POSITIVE_INFINITY;
        }
        return Math.max(MIN_WIDTH, parent.clientWidth || MIN_WIDTH);
      };

      let activePointerId: number | null = null;
      let cleanupResizeEvents: (() => void) | null = null;

      const endResize = (): void => {
        if (cleanupResizeEvents) {
          cleanupResizeEvents();
          cleanupResizeEvents = null;
        }
        activePointerId = null;
        wrapper.classList.remove('is-resizing');
        document.body.classList.remove('tiptap-image-resizing');
        document.body.style.userSelect = '';
      };

      const startResize = (event: PointerEvent, direction: ImageHandleDirection): void => {
        if (!editor.isEditable) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const target = event.currentTarget as HTMLElement | null;
        if (!target) {
          return;
        }

        activePointerId = event.pointerId;
        target.setPointerCapture?.(event.pointerId);

        const startX = event.clientX;
        const startY = event.clientY;
        const startRect = img.getBoundingClientRect();
        const startWidth = Math.max(MIN_WIDTH, startRect.width || parseDimensionToPx(currentNode.attrs['width']) || MIN_WIDTH);
        const startHeight = Math.max(MIN_HEIGHT, startRect.height || parseDimensionToPx(currentNode.attrs['height']) || MIN_HEIGHT);
        const aspectRatio = (() => {
          const nw = parseDimensionToPx(currentNode.attrs['naturalWidth']);
          const nh = parseDimensionToPx(currentNode.attrs['naturalHeight']);
          if (nw && nh) {
            return nw / nh;
          }
          if (startWidth > 0 && startHeight > 0) {
            return startWidth / startHeight;
          }
          return 1;
        })();
        const keepAspectRatio = toBoolean(currentNode.attrs['keepAspectRatio'], true);

        wrapper.classList.add('is-resizing');
        document.body.classList.add('tiptap-image-resizing');
        document.body.style.userSelect = 'none';

        const onPointerMove = (moveEvent: PointerEvent): void => {
          if (activePointerId === null || moveEvent.pointerId !== activePointerId) {
            return;
          }

          moveEvent.preventDefault();

          const dx = moveEvent.clientX - startX;
          const dy = moveEvent.clientY - startY;

          const widthAxis = direction.includes('e') ? 1 : (direction.includes('w') ? -1 : 0);
          const heightAxis = direction.includes('s') ? 1 : (direction.includes('n') ? -1 : 0);

          let nextWidth = startWidth;
          let nextHeight = startHeight;

          if (widthAxis !== 0) {
            nextWidth = startWidth + dx * widthAxis;
          }
          if (heightAxis !== 0) {
            nextHeight = startHeight + dy * heightAxis;
          }

          if (keepAspectRatio) {
            if (widthAxis !== 0 && heightAxis !== 0) {
              const horizontalDelta = dx * widthAxis;
              const verticalDelta = dy * heightAxis;

              if (Math.abs(horizontalDelta) >= Math.abs(verticalDelta)) {
                nextWidth = startWidth + horizontalDelta;
                nextHeight = nextWidth / aspectRatio;
              } else {
                nextHeight = startHeight + verticalDelta;
                nextWidth = nextHeight * aspectRatio;
              }
            } else if (widthAxis !== 0) {
              nextHeight = nextWidth / aspectRatio;
            } else if (heightAxis !== 0) {
              nextWidth = nextHeight * aspectRatio;
            } else {
              nextWidth = startWidth;
              nextHeight = startHeight;
            }
          }

          const maxWidth = getMaxWidthPx();
          nextWidth = Math.max(MIN_WIDTH, Math.min(nextWidth, maxWidth));

          if (keepAspectRatio) {
            nextHeight = nextWidth / aspectRatio;
          }

          nextHeight = Math.max(MIN_HEIGHT, nextHeight);

          if (keepAspectRatio) {
            nextWidth = Math.max(MIN_WIDTH, Math.min(nextHeight * aspectRatio, maxWidth));
          }

          img.style.width = `${Math.round(nextWidth)}px`;
          img.style.height = keepAspectRatio ? 'auto' : `${Math.round(nextHeight)}px`;
        };

        const onPointerUpOrCancel = (upEvent: PointerEvent): void => {
          if (activePointerId === null || upEvent.pointerId !== activePointerId) {
            return;
          }

          upEvent.preventDefault();

          const pos = typeof getPos === 'function' ? getPos() : null;
          if (typeof pos === 'number') {
            const widthToSave = Math.max(MIN_WIDTH, Math.round(img.getBoundingClientRect().width));
            const heightToSave = Math.max(MIN_HEIGHT, Math.round(img.getBoundingClientRect().height));
            const keepAspectRatio = toBoolean(currentNode.attrs['keepAspectRatio'], true);

            editor.chain().focus().command(({ tr, dispatch }) => {
              const existing = tr.doc.nodeAt(pos);
              if (!existing || existing.type.name !== 'image') {
                return false;
              }

              tr.setNodeMarkup(pos, undefined, {
                ...existing.attrs,
                width: `${widthToSave}px`,
                height: keepAspectRatio ? null : `${heightToSave}px`,
              });

              if (dispatch) {
                dispatch(tr);
              }
              return true;
            }).run();
          }

          endResize();
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUpOrCancel);
        window.addEventListener('pointercancel', onPointerUpOrCancel);
        cleanupResizeEvents = () => {
          window.removeEventListener('pointermove', onPointerMove);
          window.removeEventListener('pointerup', onPointerUpOrCancel);
          window.removeEventListener('pointercancel', onPointerUpOrCancel);
          if (target.hasPointerCapture?.(event.pointerId)) {
            target.releasePointerCapture(event.pointerId);
          }
        };
      };

      for (const handle of handleElements) {
        const onPointerDown: EventListener = (rawEvent: Event) => {
          startResize(rawEvent as PointerEvent, handle.dataset['handle'] as ImageHandleDirection);
        };
        addHandleListener(handle, 'pointerdown', onPointerDown);
      }

      applyImageDomFromNode(currentNode);

      return {
        dom: wrapper,
        update: (updatedNode) => {
          if (updatedNode.type.name !== this.name) {
            return false;
          }

          currentNode = updatedNode;
          applyImageDomFromNode(updatedNode);
          return true;
        },
        selectNode: () => {
          wrapper.classList.add('is-selected');
        },
        deselectNode: () => {
          wrapper.classList.remove('is-selected');
          endResize();
        },
        ignoreMutation: (mutation) => {
          const target = mutation.target;
          return target instanceof Node && (wrapper.contains(target) || target === wrapper);
        },
        destroy: () => {
          endResize();
          img.removeEventListener('dragstart', preventDrag);
          for (const listener of handleListeners) {
            listener.el.removeEventListener(listener.type, listener.fn);
          }
        },
      };
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element) => extractStyleDimension(element as HTMLElement, 'width') || (element as HTMLElement).getAttribute('width'),
        renderHTML: () => ({}),
      },
      height: {
        default: null,
        parseHTML: (element) => extractStyleDimension(element as HTMLElement, 'height') || (element as HTMLElement).getAttribute('height'),
        renderHTML: () => ({}),
      },
      keepAspectRatio: {
        default: true,
        parseHTML: (element) => toBoolean((element as HTMLElement).getAttribute('data-keep-aspect-ratio'), true),
        renderHTML: (attributes) => ({
          'data-keep-aspect-ratio': attributes['keepAspectRatio'] === false ? 'false' : 'true',
        }),
      },
      imageSourceType: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-image-source-type'),
        renderHTML: (attributes) => {
          const value = attributes['imageSourceType'];
          return value ? { 'data-image-source-type': value } : {};
        },
      },
      attachmentId: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-attachment-id'),
        renderHTML: (attributes) => {
          const value = attributes['attachmentId'];
          return value ? { 'data-attachment-id': value } : {};
        },
      },
      originalUrl: {
        default: null,
        parseHTML: (element) => (element as HTMLElement).getAttribute('data-original-url'),
        renderHTML: (attributes) => {
          const value = attributes['originalUrl'];
          return value ? { 'data-original-url': value } : {};
        },
      },
      naturalWidth: {
        default: null,
        parseHTML: (element) => {
          const v = (element as HTMLElement).getAttribute('data-natural-width');
          return v ? toPositiveNumber(v) : null;
        },
        renderHTML: (attributes) => {
          const v = toPositiveNumber(attributes['naturalWidth']);
          return v ? { 'data-natural-width': String(v) } : {};
        },
      },
      naturalHeight: {
        default: null,
        parseHTML: (element) => {
          const v = (element as HTMLElement).getAttribute('data-natural-height');
          return v ? toPositiveNumber(v) : null;
        },
        renderHTML: (attributes) => {
          const v = toPositiveNumber(attributes['naturalHeight']);
          return v ? { 'data-natural-height': String(v) } : {};
        },
      },
      // NOTE: fileSizeBytes is attachment metadata and belongs only in the
      // IndexedDB attachment record, not in the Tiptap node.
    };
  },

  renderHTML({ node, HTMLAttributes }: { node: { attrs: Record<string, unknown> }; HTMLAttributes: Record<string, unknown> }) {
    // width/height attrs use renderHTML: () => ({}) so they are absent from HTMLAttributes.
    // Read them directly from the node to produce the correct inline style.
    const width = normalizeCssDimension(node.attrs['width']);
    const height = normalizeCssDimension(node.attrs['height']);
    const keepAspectRatio = toBoolean(node.attrs['keepAspectRatio'], true);
    const styleParts = ['max-width: 100%'];

    if (width) {
      styleParts.push(`width: ${width}`);
    }
    if (!keepAspectRatio && height) {
      styleParts.push(`height: ${height}`);
    } else {
      styleParts.push('height: auto');
    }

    const mergedAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      style: styleParts.join('; '),
    });

    delete mergedAttributes['width'];
    delete mergedAttributes['height'];

    return ['img', mergedAttributes];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      insertLinkedImage:
        (payload: LinkedImageInsertPayload) =>
        ({ commands }) => {
          const safeUrl = String(payload?.url ?? '').trim();
          if (!safeUrl) {
            return false;
          }

          const attrs: Record<string, unknown> = {
            src: safeUrl,
            imageSourceType: 'linked',
            originalUrl: safeUrl,
            keepAspectRatio: true,
          };

          const nw = toPositiveNumber(payload.naturalWidth);
          const nh = toPositiveNumber(payload.naturalHeight);
          if (nw) attrs['naturalWidth'] = nw;
          if (nh) attrs['naturalHeight'] = nh;

          const w = toPositiveNumber(payload.width);
          const h = toPositiveNumber(payload.height);
          if (w) attrs['width'] = w;
          if (h && attrs['keepAspectRatio'] === false) attrs['height'] = h;

          return commands.insertContent({ type: this.name, attrs });
        },
      insertAttachmentImage:
        (attachmentResponse: AttachmentImageInsertPayload) =>
        ({ commands }) => {
          const attachmentId = String(attachmentResponse?.attachmentId ?? '').trim();
          const src = String(attachmentResponse?.src ?? attachmentResponse?.url ?? '').trim();
          if (!attachmentId || !src) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: {
              src,
              imageSourceType: 'attachment',
              attachmentId,
              keepAspectRatio: true,
            },
          });
        },
    };
  },
});
